import React, { useState } from 'react';
import { ImageIcon, UploadIcon } from 'lucide-react';
import { uploadPhoto } from '../../utils/photoUpload';

/** The shared form controls used by every Website Studio editor card. */

export function Field({
  label,
  value,
  onChange,
  icon: Icon,
  placeholder,
  helper
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  icon?: React.ComponentType<{
    className?: string;
  }>;
  placeholder?: string;
  helper?: string;
}) {
  return <label className="block" data-id="element-942" data-mp="1am5wmop4oei9">
      <span className="mb-1.5 flex items-center gap-1.5 text-[12px] font-medium text-zinc-400" data-id="element-943" data-mp="16irdhp5w7i0l">{Icon && <Icon className="h-3.5 w-3.5 text-zinc-600" data-id="element-944" data-mp="1ki0jjw0dwvqu" />}{label}</span>
      <input value={value} onChange={event => onChange(event.target.value)} placeholder={placeholder} className="w-full rounded-xl border border-white/[0.1] bg-ink-800 px-3 py-2.5 text-[13px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70 focus:ring-2 focus:ring-[#d97742]/15" data-id="element-945" data-mp="1kg68raituk0h" />
      {helper && <span className="mt-1.5 block text-[11px] leading-relaxed text-zinc-600" data-id="element-946" data-mp="298zp1eusmf4a">{helper}</span>}
    </label>;
}
export function TextField({
  label,
  value,
  onChange
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
}) {
  return <label className="block" data-id="element-947" data-mp="2i8w6owirmdpu">
      <span className="mb-1.5 block text-[12px] font-medium text-zinc-400" data-id="element-948" data-mp="2i7l4v28vrg1d">{label}</span>
      <textarea value={value} onChange={event => onChange(event.target.value)} rows={3} className="w-full resize-y rounded-xl border border-white/[0.1] bg-ink-800 px-3 py-2.5 text-[13px] leading-relaxed text-white outline-none focus:border-[#d97742]/70 focus:ring-2 focus:ring-[#d97742]/15" data-id="element-949" data-mp="2n1pczotvtu41" />
    </label>;
}
export function ImageField({
  label,
  value,
  onChange,
  alt
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  alt: string;
}) {
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const upload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setError('');
    setBusy(true);
    const result = await uploadPhoto(file);
    setBusy(false);
    if (result.url) onChange(result.url);else setError(result.error ?? 'Could not read that image.');
  };
  return <div className="mt-2 rounded-xl border border-white/[0.08] bg-ink-850 p-2.5" data-id="element-950" data-mp="2jvhlarhh5yop">
      <span className="mb-2 flex items-center gap-1.5 text-[12px] font-medium text-zinc-400" data-id="element-951" data-mp="2xv0ttle77c1j"><ImageIcon className="h-3.5 w-3.5 text-zinc-600" data-id="element-952" data-mp="2xsxsi57lhwhi" />{label}</span>
      <div className="flex items-start gap-3" data-id="element-953" data-mp="2sv4lj9ygwyg6">
        {value ? <img src={value} alt={alt} className="h-16 w-16 shrink-0 rounded-lg border border-white/10 object-cover" data-id="element-954" data-mp="37lc0hfp9nzc4" /> : <div className="flex h-16 w-16 shrink-0 items-center justify-center rounded-lg border border-dashed border-white/[0.14] bg-ink-800" data-id="element-955" data-mp="32m940jncr1fr"><ImageIcon className="h-4 w-4 text-zinc-600" data-id="element-956" data-mp="3gj5rqc86c2sf" /></div>}
        <div className="min-w-0 flex-1" data-id="element-957" data-mp="3bo08xma2j48k">
          <div className="flex flex-wrap items-center gap-2" data-id="element-958" data-mp="3v87en6qydij9">
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-lg bg-[#d97742]/15 px-2.5 py-1.5 text-[12px] font-medium text-[#e8a369] transition-colors duration-150 ease-out hover:bg-[#d97742]/25" data-id="element-959" data-mp="3v90ednrhv7rw">
              <UploadIcon className="h-3.5 w-3.5" data-id="element-960" data-mp="3m9vf2f83zaeo" /> {busy ? 'Uploading…' : value ? 'Replace' : 'Upload'}
              <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={upload} className="sr-only" data-id="element-961" data-mp="3chpqzgkz9d4r" />
            </label>
            {value && <button type="button" onClick={() => {
            onChange('');
            setError('');
          }} className="rounded-lg px-2 py-1.5 text-[11.5px] font-medium text-zinc-500 transition-colors hover:text-danger" data-id="element-962" data-mp="3h9qyyqb44hoz">Remove</button>}
          </div>
          <input value={value.startsWith('data:') ? '' : value} onChange={event => {
          onChange(event.target.value);
          setError('');
        }} placeholder={value.startsWith('data:') ? 'Uploaded image' : 'Or paste a hosted image URL'} className="mt-2 w-full rounded-lg border border-white/[0.1] bg-ink-800 px-2.5 py-2 text-[12px] text-white outline-none placeholder:text-zinc-600 focus:border-[#d97742]/70" data-id="element-963" data-mp="2tj3qa2tdho1d" />
        </div>
      </div>
      <p className="mt-2 text-[10.5px] leading-relaxed text-zinc-600" data-id="element-964" data-mp="2gqnr39z69k7q">Uploads are resized and saved to your cloud storage, so they show on every device · PNG, JPG, WebP, or GIF</p>
      {error && <p role="alert" className="mt-1.5 text-[11px] text-rose-300" data-id="element-965" data-mp="27raiejivgzo2">{error}</p>}
    </div>;
}